import * as React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetListPointsOfInterest } from 'src/features/parkingManagement/api/PointOfInterestsAPIs/useGetListPointsOfInterest';
import { useGetParkingPointOfInterest } from 'src/features/parkingManagement/api/PointOfInterestsAPIs/useGetParkingsPointOfInterest';
import { useUpdateParking } from 'src/features/parkingManagement/api/updateParking';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import AddNewPointOfInterest from 'src/features/parkingManagement/components/PointsOfInterest/AddNewPointOfInterest';
import DeletePointOfInterestModal from 'src/features/parkingManagement/components/PointsOfInterest/DeletePointOfInterestModal';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils/';
import { PointsOfInterestTableColumns } from 'src/features/parkingManagement/utils/pointsOfInterestTableColumns';
import { GeneralBlockSubtitle, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import { DataTable } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalErrorMessages, generalLabelMessages } from 'src/utils/messages';

import type { ParkingPointsOfInterest } from 'src/features/parkingManagement/types/parking';

const PointsOfInterest = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });
    const { setTitle, removeTitle } = useTitleStore();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState<boolean>(false);
    const [selectedPointOfInterest, setSelectedPointOfInterest] = React.useState<ParkingPointsOfInterest | undefined>(
        undefined,
    );

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const { data: listPointOfInterest } = useGetListPointsOfInterest({
        config: { enabled: false },
    });

    const { data: parkingPointsOfInterestData, refetch } = useGetParkingPointOfInterest({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const parkingPointOfInterest = React.useMemo(() => {
        return (parkingPointsOfInterestData?.point_of_interest_distances ?? [])
            .map((point) => listPointOfInterest?.results.find((obj) => obj.id === point.point_of_interest.id))
            .filter((item): item is ParkingPointsOfInterest => item !== undefined);
    }, [parkingPointsOfInterestData?.point_of_interest_distances, listPointOfInterest]);

    const tableData = React.useMemo(() => {
        return (parkingPointOfInterest ?? []).filter((item): item is ParkingPointsOfInterest => item !== undefined);
    }, [parkingPointOfInterest]);

    const currentPointsOfInterest = React.useMemo(() => {
        return parkingPointsOfInterestData?.point_of_interest_distances.map((item) => item.point_of_interest.id) ?? [];
    }, [parkingPointsOfInterestData?.point_of_interest_distances]);

    const handleDelete = (point?: ParkingPointsOfInterest, isOpen: boolean = false) => {
        setSelectedPointOfInterest(point);
        setIsDeleteModalOpen(isOpen);
    };

    const openDeleteModal = (point: ParkingPointsOfInterest) => handleDelete(point, true);
    const closeDeleteModal = () => handleDelete(undefined);

    const columns = PointsOfInterestTableColumns({ openDeleteModal });

    const { mutate: deletePointOfInterest } = useUpdateParking({
        onSuccess: () => {
            void refetch();
            toast({
                title: formatMessage(parkingManagementMessages.deletedSuccessfully),
                description: formatMessage(parkingManagementMessages.deletedSuccessfullyDescription),
            });
        },
        onError: (e) => {
            if (e?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${e?.response?.data?.detail}`,
                });
            }
        },
    });

    const onSubmit = (selectedPoint: ParkingPointsOfInterest) => {
        const updatedPointsOfInterest = currentPointsOfInterest.filter((item) => item !== selectedPoint?.id);

        deletePointOfInterest({
            parkingId: parkingId,
            point_of_interests: updatedPointsOfInterest,
        });
    };

    return (
        <>
            <div className="flex flex-col gap-2">
                <GeneralBlockTitle>{formatMessage(parkingManagementMessages.pointsOfInterest)}</GeneralBlockTitle>
                <GeneralBlockSubtitle>
                    {formatMessage(parkingManagementMessages.pointsOfInterestSubtitle)}
                </GeneralBlockSubtitle>
            </div>
            <DataTable
                columns={columns}
                data={{
                    count: tableData?.length || 0,
                    results: tableData,
                }}
                semantics={{
                    title: formatMessage(parkingManagementMessages.pointsOfInterest),
                    entryName: formatMessage(parkingManagementMessages.pointOfInterest),
                    entryNames: formatMessage(parkingManagementMessages.pointsOfInterest),
                }}
                components={{
                    newEntry: AddNewPointOfInterest,
                }}
                enableLocalFilters={true}
            />
            {isDeleteModalOpen && selectedPointOfInterest && (
                <DeletePointOfInterestModal
                    isDeleteModalOpen={isDeleteModalOpen}
                    closeDeleteModal={closeDeleteModal}
                    selectedPointOfInterest={selectedPointOfInterest}
                    deletePointOfInterest={() => onSubmit(selectedPointOfInterest)}
                />
            )}
        </>
    );
};

export default PointsOfInterest;
